import React, { FC, useMemo } from 'react';
import { ButtonStroke } from '@blink/components';
import { ReactComponent as LogoIcon } from '@blink/assets/src/icons/logoInCircle.svg';
import { Progress } from '@blink/components/src/Progress/Progress';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonSize } from '@blink/components/src/Button/Button.types';
import { useNavigate } from 'react-router-dom';

import { useAssetsToLendRowsStyle } from './AssetsToLendItemRow.style';

import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { Store } from '@/shared/store';
import { getAgreementsAction } from '@/shared/store/agreements/actions';
import { formatSmart, formatWithMaxPrecision } from '@/shared/utils/helpers';
import { Agreement, ROLE } from '@/shared/api/borrow';
import { Vault } from '@/shared/api/vault';
import { getVaultPools } from '@/shared/store/vaults/actions';
import { AssetWithIcon } from '@/shared/ui';

type AssetsToLendRowsProps = {
    allItems: Agreement[] | any;
    itemIndex: number;
    vault: Vault;
    role: ROLE;
};

export const AssetsToLendItemRow: FC<AssetsToLendRowsProps> = ({
    itemIndex,
    allItems,
    role,
    vault,
}) => {
    const classes = useAssetsToLendRowsStyle();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const agreement = allItems[itemIndex];
    const { address, title, leverage, apy, userValue, totalDeposited, totalDepositThreshold } =
        agreement;
    const formattedApyRewards = Number(apy).toFixed(2);
    const currentApy = Number(formattedApyRewards);
    const totalSupply = formatWithMaxPrecision(
        agreement.totalDepositThreshold ?? '0',
        leverage?.decimals,
    );

    const { marginAccounts, walletAddress } = useSelector((state: Store) => ({
        marginAccounts: state.userVaults.marginAccounts,
        walletAddress: state.wallets.active.address,
    }));

    const currentAccount = useMemo(
        () => marginAccounts?.find((account) => account.agreementAddress === vault.address),
        [marginAccounts, vault.address],
    );

    const refresh = async () => {
        dispatch(
            getAgreementsAction({
                role,
                withoutCache: true,
            }),
        );

        dispatch(
            getVaultPools({
                vaultAddress: vault.address,
                marginAccountAddress: currentAccount?.address as string,
                withoutCache: true,
            }),
        );
    };

    const handleSupplyClick = () => {
        return dispatch(
            showModalAction({
                modalName: ModalName.MARGIN_ACCOUNTS_VAULT_SUPPLY_MODAL,
                modalTitle: 'Supply Liquidity',
                modalProps: {
                    tokenSymbol: leverage.symbol,
                    balance: userValue,
                    currentApy,
                    totalDeposited: totalDeposited,
                    totalDepositThreshold: totalSupply,
                    agreementAddress: address,
                    marginAccountAddress: currentAccount?.address,
                    tokenAddress: leverage.address,
                    isSupply: true,
                    decimals: leverage.decimals,
                    refresh: refresh,
                    poolUtilization: agreement!.poolUtilization,
                    apy: agreement.apy,
                    userValue: userValue,
                },
            }),
        );
    };

    const supplyProgress = Math.round(
        (Number(totalDeposited) / Number(totalDepositThreshold)) * 100,
    );

    const handleDetails = () => {
        return navigate(`/pool/${address}?role=lender`);
    };

    if (!leverage) return null;

    return (
        <>
            <tr className={classes.tr}>
                <td className={classes.firstTd}>{title}</td>
                <td>
                    <AssetWithIcon assetName={leverage.symbol} />
                </td>
                <td>
                    <div className={classes.progressWrapper}>
                        <div>{`${formatSmart(
                            formatWithMaxPrecision(
                                agreement.totalDepositThreshold ?? '0',
                                leverage.decimals,
                            ),
                        )} ${leverage.symbol}`}</div>
                        <Progress percent={supplyProgress} />
                    </div>
                </td>
                <td>
                    <div>{currentApy}%</div>
                    <div className={classes.supplyApyGroup}>
                        <div>{`${Number(apy)}%`}</div>
                        <LogoIcon />
                    </div>
                </td>
                <td>
                    <div>{`${userValue ?? 'N/A'} ${leverage.symbol}`}</div>
                </td>
                <td>
                    <div className={classes.buttonWrapper}>
                        <ButtonStroke
                            isPrimary={true}
                            size={ButtonSize.md}
                            className={classes.supplyButton}
                            onClick={handleSupplyClick}
                            disabled={!walletAddress}
                        >
                            {'Supply'}
                        </ButtonStroke>
                        <ButtonStroke
                            size={ButtonSize.md}
                            className={classes.detailsButton}
                            onClick={handleDetails}
                        >
                            Details
                        </ButtonStroke>
                    </div>
                </td>
            </tr>
        </>
    );
};
